/**
 * Custom hook for building filter props objects
 * Extracted from the main component to improve modularity and testability
 */
import { useMemo } from 'react';
import {
    formatFinalSchema,
    formatFinalQuickSchema,
    formatFinalCrossSchema,
} from '../utils/schemaFormatters';

/**
 * Custom hook for building memoized filter props
 * @param {Object} config - Configuration object containing all necessary props
 * @param {string[]} [config.readOnlyFilterIds] - From readOnlyFilters; non-editable in QuickFilters and Filters UI
 * @returns {Object} Object containing different prop configurations
 */
const useFiltersProps = ({
    // Common props
    entity,
    values,
    operators,
    crossOperators,
    formatCustomView,
    formatViews,
    getAllowedOperators,
    getFieldsWithoutAdvancedFilters,
    getHasAdvancedFilters,
    onChangeFilter,
    onChangeCustomView,
    onChangeView,
    onClearFilters,
    handleChangeOperator,
    forceSelectAllTypes,

    // Quick filters specific
    className,
    crossLabels,
    filterDragging,
    quickFilters,
    getCanClearAll,
    hasFiltersActive,
    license,
    onToggleFilter,
    init,
    openQuickFilters,
    useQuickView,
    entityToForceLabel,

    // Quick filters add props
    showQuickFiltersAdd,
    useAddIcon,
    newDefaultFilters,
    extraFiltersSchema,
    onAddQuickFilter,
    onDeleteMultipleFilters,
    onResetVisibles,
    visibleFilters,
    hasCrossFilters,
    getModalOptionsSchema,
    state,
    getExtraFiltersSchema,

    // Standard filters specific
    viewsPrivateManagement,
    viewsPublicManagement,
    isOpen,
    onClose,
    onAddFilter,
    onDeleteFilter,
    onDeleteCustomView,
    onDeleteView,
    onDrag,
    onDrop,
    onReOrder,
    onSaveView,
    toggleQuickFilter,
    avoidQuickFilters,
    useDrawer,
    useViewsInFilters,
    useViewsInMenu,
    openFilters,
    filtersToIgnore,
    useViews,

    // Schema formatting dependencies
    mappedSchema,
    hiddenFilters,
    getFieldsFormatted,
    getSubfilterSchema,
    crossSchema,
    getFinalCrossSchema,
    mappedQuickFilters,
    mappedVisibleFilters,
    readOnlyFilterIds = [],

    // Additional props to pass through
    additionalProps,
}) => {
    // Format schemas
    const formatFinalSchemaResult = useMemo(() => {
        return formatFinalSchema({
            visibleFilters,
            quickFilters,
            mappedSchema,
            hiddenFilters,
            getFieldsFormatted,
            getSubfilterSchema,
            readOnlyFilterIds,
        });
    }, [
        visibleFilters,
        quickFilters,
        mappedSchema,
        hiddenFilters,
        getFieldsFormatted,
        getSubfilterSchema,
        readOnlyFilterIds,
    ]);

    const formatFinalQuickSchemaResult = useMemo(() => {
        return formatFinalQuickSchema({
            quickFilters,
            mappedSchema,
            crossSchema,
            hiddenFilters,
            getFieldsFormatted,
            getSubfilterSchema,
            crossOperators,
            readOnlyFilterIds,
        });
    }, [
        quickFilters,
        mappedSchema,
        crossSchema,
        hiddenFilters,
        getFieldsFormatted,
        getSubfilterSchema,
        crossOperators,
        readOnlyFilterIds,
    ]);

    const formatFinalCrossSchemaResult = useMemo(() => {
        return formatFinalCrossSchema({
            crossSchema,
            getFinalCrossSchema,
            quickfilters: mappedQuickFilters,
            visibleFilters: mappedVisibleFilters,
            getFieldsFormatted,
            getSubfilterSchema,
        });
    }, [
        crossSchema,
        getFinalCrossSchema,
        mappedQuickFilters,
        mappedVisibleFilters,
        getFieldsFormatted,
        getSubfilterSchema,
    ]);

    // Common props shared between quick and standard filters
    const commonProps = useMemo(
        () => ({
            crossOperators,
            customView: formatCustomView(values?.customView || null),
            entity,
            getAllowedOperators,
            getFieldsWithoutAdvancedFilters,
            getHasAdvancedFilters,
            onChange: onChangeFilter,
            onChangeCustomView,
            onChangeOperator: handleChangeOperator,
            onChangeView,
            onClearAll: onClearFilters,
            operators,
            selectedView: values?.customView || values?.idView || null,
            values,
            views: formatViews,
            forceSelectAllTypes,
        }),
        [
            crossOperators,
            entity,
            getAllowedOperators,
            getFieldsWithoutAdvancedFilters,
            getHasAdvancedFilters,
            onChangeFilter,
            onChangeCustomView,
            handleChangeOperator,
            onChangeView,
            onClearFilters,
            operators,
            values,
            formatViews,
            formatCustomView,
            forceSelectAllTypes,
        ],
    );

    // Quick filters base props
    const quickFiltersBaseProps = useMemo(
        () => ({
            className,
            crossLabels,
            filterDragging,
            filters: quickFilters,
            getCanClearAll,
            hasFiltersActive,
            license,
            onToggle: onToggleFilter,
            resetFilter: () => init(true),
            schema: formatFinalQuickSchemaResult,
            toggled: openQuickFilters,
            useQuickView,
            entityToForceLabel,
        }),
        [
            className,
            crossLabels,
            filterDragging,
            quickFilters,
            getCanClearAll,
            hasFiltersActive,
            license,
            onToggleFilter,
            init,
            formatFinalQuickSchemaResult,
            openQuickFilters,
            useQuickView,
            entityToForceLabel,
        ],
    );

    // Quick filters add props
    const quickFiltersAddProps = useMemo(() => {
        if (!showQuickFiltersAdd) return {};

        let props = {
            showAdd: showQuickFiltersAdd,
            useAddIcon,
            defaultFilters: newDefaultFilters,
            extraSchema: extraFiltersSchema,
            onConfirmAdd: onAddQuickFilter,
            onDeleteMultiple: onDeleteMultipleFilters,
            onResetVisibles,
            visibleFilters,
            readOnlyFilterIds,
        };

        if (hasCrossFilters) {
            props = {
                ...props,
                ...getModalOptionsSchema({
                    crossSchema: state.crossSchema,
                    extraSchema: extraFiltersSchema,
                    getExtraFiltersSchema,
                }),
            };
        }

        return props;
    }, [
        showQuickFiltersAdd,
        useAddIcon,
        newDefaultFilters,
        extraFiltersSchema,
        onAddQuickFilter,
        onDeleteMultipleFilters,
        onResetVisibles,
        visibleFilters,
        readOnlyFilterIds,
        hasCrossFilters,
        getModalOptionsSchema,
        state,
        getExtraFiltersSchema,
    ]);

    // Standard filters props
    const standardFiltersProps = useMemo(() => {
        let props = {
            canManagePrivateViews: viewsPrivateManagement,
            canManagePublicViews: viewsPublicManagement,
            crossSchema: formatFinalCrossSchemaResult,
            defaultFilters: newDefaultFilters,
            extraSchema: extraFiltersSchema,
            isOpen,
            onClose,
            onConfirmAdd: onAddFilter,
            onDelete: onDeleteFilter,
            onDeleteCustomView,
            onDeleteMultiple: onDeleteMultipleFilters,
            onDeleteView,
            onDrag,
            onDrop,
            onReOrder,
            onResetVisibles,
            onSaveView: useViews && onSaveView ? onSaveView : undefined,
            onToggle: onToggleFilter,
            quickFilters,
            quickSchema: formatFinalQuickSchemaResult,
            schema: formatFinalSchemaResult,
            showAdd: true,
            toggled: openFilters,
            toggleQuickFilter,
            avoidQuickFilters,
            useDrawer,
            useViewsInFilters,
            useViewsInMenu,
            visibleFilters,
            readOnlyFilterIds,
        };

        if (hasCrossFilters) {
            props = {
                ...props,
                ...getModalOptionsSchema({
                    crossSchema: state.crossSchema,
                    extraSchema: extraFiltersSchema,
                    getExtraFiltersSchema,
                    hiddenFilters: filtersToIgnore,
                }),
            };
        }

        return props;
    }, [
        viewsPrivateManagement,
        viewsPublicManagement,
        formatFinalCrossSchemaResult,
        newDefaultFilters,
        extraFiltersSchema,
        isOpen,
        onClose,
        onAddFilter,
        onDeleteFilter,
        onDeleteCustomView,
        onDeleteMultipleFilters,
        onDeleteView,
        onDrag,
        onDrop,
        onReOrder,
        onResetVisibles,
        useViews,
        onSaveView,
        onToggleFilter,
        quickFilters,
        formatFinalQuickSchemaResult,
        formatFinalSchemaResult,
        openFilters,
        toggleQuickFilter,
        avoidQuickFilters,
        useDrawer,
        useViewsInFilters,
        useViewsInMenu,
        visibleFilters,
        hasCrossFilters,
        getModalOptionsSchema,
        state,
        getExtraFiltersSchema,
        filtersToIgnore,
        readOnlyFilterIds,
    ]);

    return {
        commonProps,
        quickFiltersBaseProps,
        quickFiltersAddProps,
        standardFiltersProps,
        formatFinalSchemaResult,
        formatFinalQuickSchemaResult,
        formatFinalCrossSchemaResult,
    };
};

export default useFiltersProps;
